import React, { createContext, useContext, useState } from "react";
import env from "@common/environment";

interface I_OCRResult {
  ocr_face_image: string;
  ocr_masking_image: string;
  ocr_origin_image: string;
  ocr_result: {
    // 공통
    result_scan_type: string;
    name: string;
    issued_date?: string;
    complete?: string;
    birth?: string;
    id_truth?: string;
    id_truth_retry_count?: number;
    fd_confidence?: string;

    // 주민등록증, 운전면허증, 국내여권, 외국인등록증
    jumin?: string;
    masked_jumin?: string;

    // 주민등록증, 운전면허증
    region?: string;

    // 운전면허증
    driver_number?: string;
    masked_driver_number?: string;
    driver_serial?: string;
    driver_type?: string;

    // 국내여권
    sur_name?: string;
    given_name?: string;
    passport_type?: string;
    issuing_country?: string;
    passport_number?: string;
    sex?: string;
    expiry_date?: string;
    birthday?: string;

    // 외국인등록증
    name_kor?: string;
    nationality?: string;
    visa_type?: string;
  };
  ocr_type: string;
  ssa_mode: boolean;
  // ssa_result: {
  //   fd_confidence: string;
  //   id_truth: string;
  //   id_truth_retry_conut: number
  // }
}
interface I_OCRContext {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  config: any;
  ocrType: T_OCR_TYPE;
  result: I_OCRResult;
  croppedImage: string;
  handlers: {
    reset: () => void;
    setOcrType: (ocrType: T_OCR_TYPE) => void;
    setResult: (ocrResult: I_OCRResult) => void;
    setCroppedImage: (croppedImage: string) => void;
  };
}

const Context = createContext<I_OCRContext | null>(null);

export const useOCRContext = () => {
  const context = useContext(Context);
  if (!context) throw Error("ocr context is null");
  return context;
};

const config = {
  licenseKey: env.ENV_OCR_LICENSE_KEY,
  resourceBaseUrl: env.ENV_OCR_RESOURCE_BASE_URL,
  ocrServerBaseUrl: env.ENV_MI_OCR_SERVER_BASE_URL,
  useTopUI: false,
  useTopUITextMsg: false,
  useMiddleUI: false,
  useMiddleUITextMsg: false,
  usePreviewUI: false,
  maskFrameStyle: {
    base_color: "#f7f8fc",
    not_ready: "#f7f8fc",
    ready: "#f7f8fc",
    detect_success: "#f7f8fc",
    detect_failed: "#f7f8fc",
    recognized: "#f7f8fc",
    recognized_with_ssa: "#f7f8fc",
    ocr_success: "#f7f8fc",
    ocr_success_with_ssa: "#f7f8fc",
    ocr_failed: "#f7f8fc",
  },
  frameBorderStyle: {
    width: 2,
    radius: 5,
    ready: "#00000000",
    detect_success: "#4e42ec",
  },
};

const defaultContextValue = {
  config,
  ocrType: "idcard-ssa" as T_OCR_TYPE,
  result: {
    ocr_face_image: "",
    ocr_masking_image: "",
    ocr_origin_image: "",
    ocr_result: {},
    ocr_type: "",
    ssa_mode: false,
  } as I_OCRResult,
  croppedImage: "",
};

const Provider = ({ children }: { children: React.ReactNode }) => {
  const [state, setState] = useState(defaultContextValue);

  const setOcrType = (ocrType: T_OCR_TYPE) => {
    setState((prev) => ({ ...prev, ocrType }));
  };

  const setResult = (ocrResult: I_OCRResult) => {
    setState((prev) => ({ ...prev, result: { ...ocrResult } }));
  };

  const setCroppedImage = (croppedImage: string) => {
    setState((prev) => ({ ...prev, croppedImage }));
  };

  const reset = () => {
    setState(defaultContextValue);
  };

  const handlers = { reset, setOcrType, setResult, setCroppedImage };

  return <Context.Provider value={{ ...state, handlers }}>{children}</Context.Provider>;
};

export default { Context, Provider };
